// File: frontend/src/utils/slugGenerator.ts
// src/utils/slugGenerator.ts
import { MatchModel } from "../models";

const turkishToEnglishMap: Record<string, string> = {
  ğ: "g",
  ü: "u",
  ş: "s",
  ı: "i",
  ö: "o",
  ç: "c",
  Ğ: "g",
  Ü: "u",
  Ş: "s",
  İ: "i",
  Ö: "o",
  Ç: "c",
  " ": "-",
};

const normalizeTurkishChars = (str: string): string => {
  return str
    .split("")
    .map((char) => turkishToEnglishMap[char] || char)
    .join("");
};

// Generate slug from username for profile URLs
export const generateUsernameSlug = (username: string): string => {
  // Normalize Turkish chars (map covers both upper/lowercase forms) BEFORE lowercasing.
  // JS toLowerCase() turns İ into "i" + a combining dot above (U+0307) instead of a plain
  // "i", which the map below could never match afterward — that leftover combining mark
  // then got treated as a separator and injected a stray hyphen into the slug. Running the
  // map first, on the original-case string, avoids the bad decomposition entirely.
  return normalizeTurkishChars(username)
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-") // Replace special chars with hyphen
    .replace(/-+/g, "-") // Replace multiple hyphens with one
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

// Generate full profile URL path: /yazar/{slug}--{id}
export const generateProfileUrl = (username: string, userId: number): string => {
  const slug = generateUsernameSlug(username);
  return `/yazar/${slug}--${userId}`;
};

export const generateMatchSlug = (match: MatchModel): string => {
  // Use the permanent server-generated slug when available.
  // This is set once at match creation and never changes — even if club
  // short names are later edited in the admin panel.
  if (match.slug) return match.slug;

  // Fallback: generate slug on the fly (used for matches created before the
  // slug column was added, or for partial match objects in search results).
  const date =
    match.matchDate instanceof Date
      ? match.matchDate
      : new Date(match.matchDate);

  // Turkish month names (we'll normalize them below)
  const turkishMonths = [
    "ocak",
    "şubat",
    "mart",
    "nisan",
    "mayıs",
    "haziran",
    "temmuz",
    "ağustos",
    "eylül",
    "ekim",
    "kasım",
    "aralık",
  ];

  const day = date.getDate();
  const month = normalizeTurkishChars(turkishMonths[date.getMonth()]); // NORMALIZE TURKISH MONTH NAMES
  const year = date.getFullYear();

  // Process team names
  // Normalize Turkish chars BEFORE lowercasing — see generateUsernameSlug above for why
  // (JS toLowerCase() mangles İ into i + a combining dot, which the map can't catch after the fact).
  const processTeamName = (name: string) => {
    return normalizeTurkishChars(name).toLowerCase()
      .replace(/[^a-z0-9-]/g, "") // Remove special chars
      .replace(/-+/g, "-") // Replace multiple hyphens with one
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
  };

  const homeTeam = processTeamName(match.homeTeam);
  const awayTeam = processTeamName(match.awayTeam);

  // Include matchId at the end for reliable routing: {slug}--{id}
  return `${day}-${month}-${year}-${homeTeam}-${awayTeam}-maci--${match.id}`;
};

/**
 * Extract matchId from slug format: "29-aralik-2025-besiktas-caykur-rizespor-maci--123"
 * Returns null if no valid id found
 */
export const extractMatchIdFromSlug = (slug: string): number | null => {
  const parts = slug.split("--");
  if (parts.length < 2) return null;
  
  const id = parseInt(parts[parts.length - 1], 10);
  return isNaN(id) ? null : id;
};

export const findMatchBySlug = (
  matches: MatchModel[],
  slug: string
): MatchModel | undefined => {
  // First try to find by matchId (more reliable)
  const matchId = extractMatchIdFromSlug(slug);
  if (matchId !== null) {
    const matchById = matches.find((match) => match.id === matchId);
    if (matchById) return matchById;
  }
  
  // Fallback: try to match by generating slug (for backwards compatibility)
  return matches.find((match) => generateMatchSlug(match) === slug);
};

/**
 * Generate match URL from entry data (homeTeam, awayTeam, matchDate, matchId).
 * Used in profile page to link entries to their match pages.
 */
export const generateMatchUrlFromEntry = (
  homeTeam: string,
  awayTeam: string,
  matchDate: Date,
  matchId: number
): string => {
  const date = matchDate instanceof Date ? matchDate : new Date(matchDate);

  // Turkish month names (we'll normalize them below)
  const turkishMonths = [
    "ocak",
    "şubat",
    "mart",
    "nisan",
    "mayıs",
    "haziran",
    "temmuz",
    "ağustos",
    "eylül",
    "ekim",
    "kasım",
    "aralık",
  ];

  const day = date.getDate();
  const month = normalizeTurkishChars(turkishMonths[date.getMonth()]);
  const year = date.getFullYear();

  // Process team names
  // Normalize Turkish chars BEFORE lowercasing — see generateUsernameSlug above for why
  // (JS toLowerCase() mangles İ into i + a combining dot, which the map can't catch after the fact).
  const processTeamName = (name: string) => {
    return normalizeTurkishChars(name).toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const home = processTeamName(homeTeam);
  const away = processTeamName(awayTeam);
  const slug = `${day}-${month}-${year}-${home}-${away}-maci--${matchId}`;

  return `/${slug}`;
};